/**
 * F09 v2 — `/verifications` module home: tabs Requests | Candidates | Watchlist.
 *
 * Route: /verifications (gated `verifications:read` in App.tsx). The active tab
 * is synced to `?tab=` so links land on the right tab. Header actions link to
 * the QA page and (super_admin only) the direct-verify page.
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Button, Tab, TabList, TabPanel, Tabs } from '@bravemove/ui-web';
import { PageHeader } from '../../components/PageHeader';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import { CandidatesTab } from './CandidatesTab';
import { RequestsTab } from './RequestsTab';
import { WatchlistTab } from './WatchlistTab';

const TABS = ['requests', 'candidates', 'watchlist'] as const;
type TabKey = (typeof TABS)[number];

function isTab(v: string | null): v is TabKey {
  return v !== null && (TABS as readonly string[]).includes(v);
}

export function VerificationsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canIssue } = useVerificationPermissions();
  const raw = searchParams.get('tab');
  const tab: TabKey = isTab(raw) ? raw : 'requests';

  const onTabChange = (next: string) => {
    if (!isTab(next)) return;
    // Switching tabs drops the other tabs' filters (each tab owns its params).
    setSearchParams(next === 'requests' ? {} : { tab: next });
  };

  return (
    <div className="space-y-5" data-testid="verifications-page">
      <PageHeader
        title={t('admin:verification.pageTitle.module')}
        actions={
          <>
            <Button size="sm" variant="ghost" onClick={() => navigate('/verifications/qa')}>
              {t('admin:verification.pageTitle.qa')}
            </Button>
            {canIssue ? (
              <Button
                size="sm"
                variant="secondary"
                icon="shield"
                data-testid="verifications-direct-link"
                onClick={() => navigate('/verifications/direct')}
              >
                {t('admin:verification.pageTitle.direct')}
              </Button>
            ) : null}
          </>
        }
      />
      <Tabs value={tab} defaultValue="requests" onValueChange={onTabChange} variant="line">
        <TabList aria-label={t('admin:verification.tabs.ariaLabel')}>
          <Tab value="requests">{t('admin:verification.tabs.requests')}</Tab>
          <Tab value="candidates">{t('admin:verification.tabs.candidates')}</Tab>
          <Tab value="watchlist">{t('admin:verification.tabs.watchlist')}</Tab>
        </TabList>
        <TabPanel value="requests" className="pt-4">
          <RequestsTab />
        </TabPanel>
        <TabPanel value="candidates" className="pt-4">
          <CandidatesTab />
        </TabPanel>
        <TabPanel value="watchlist" className="pt-4">
          <WatchlistTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}
